import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Home, Train, TrendingUp, Crown, MessageCircle, CheckCircle, BadgeCheck, Heart } from "lucide-react";
import { formatINR, formatDistance, resolveImage, formatSize } from "../lib/api";
import api from "../lib/api";
import { buildPropertyShareText, whatsappLink } from "../lib/brand";
import { useAuth } from "../lib/AuthContext";
import AuthGate from "../components/AuthGate";
import AddToShortlistButton from "../components/AddToShortlistButton";
import { normalizePropertyType, normalizeBhk, buildDisplayTags } from "../shared/utils/propertyNormalize";
import { toast } from "sonner";

const TagPill = ({ label }) => {
  const cls =
    label === "Near Metro" ? "tag-metro"
    : label === "Best Deal" ? "tag-best"
    : label === "Premium Deal" ? "bg-[#0055FF] text-white border border-[#0055FF]"
    : label === "Verified" ? "bg-[#F0FDF4] text-[#15803D] border border-[#BBF7D0]"
    : "tag-roi";
  const Icon =
    label === "Near Metro" ? Train :
    label === "Premium Deal" ? Crown :
    label === "Verified" ? BadgeCheck :
    TrendingUp;
  return (
    <span
      className={`${cls} inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm`}
      data-testid={`tag-${label.toLowerCase().replace(/ /g, "-")}`}
    >
      <Icon size={10} /> {label}
    </span>
  );
};

export default React.memo(function PropertyCard({ p, index = 0, onSaveChange }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [showGate, setShowGate] = useState(false);
  const [saved, setSaved] = useState(!!p.is_saved);
  const [savedCount, setSavedCount] = useState(p.saved_count || 0);
  const [savingFav, setSavingFav] = useState(false);
  const isAnon = !user;
  // v2.71.80 — Use the resize variant for card thumbnails. 600w covers up
  // to 2× DPI for cards displayed at ~300 CSS px wide. `srcset` lets the
  // browser pick 600w/900w based on device pixel ratio. Cuts payload from
  // ~3 MB → ~15 KB (WebP) per card.
  const img = p.images && p.images[0] ? resolveImage(p.images[0], { w: 600 }) : null;
  const imgSrcSet = p.images && p.images[0]
    ? `${resolveImage(p.images[0], { w: 600 })} 600w, ${resolveImage(p.images[0], { w: 900 })} 900w`
    : null;
  const isSold = p.status === "Sold";

  // v2.71.21 — Skip entrance animation past the first 12 cards. Below-the-fold
  // cards don't need to "rise in" — saves compositor cost on long lists.
  const animate = index < 12;

  const onShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // v2.40 — track whatsapp click as a high-intent behavioural signal
    api.post("/leads/track", {
      kind: "whatsapp_click",
      ref_id: p.id,
      meta: { title: p.title, area: p.main_area || p.area, bhk: p.bhk, property_type: p.property_type },
    }).catch(() => {});
    // Use SSR /api/share/property/{id} URL — backend renders OG meta tags so
    // WhatsApp / FB / Twitter previews show property image + title + price.
    // (Bots fetch the URL; humans get a meta-refresh redirect to /property/{id}.)
    const link = `${window.location.origin}/api/share/property/${p.id}`;
    window.open(whatsappLink(buildPropertyShareText(p, link)), "_blank", "noopener,noreferrer");
  };

  const onToggleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isAnon) { setShowGate(true); return; }
    if (savingFav) return;
    setSavingFav(true);
    // Optimistic UI
    const optimistic = !saved;
    setSaved(optimistic);
    setSavedCount((c) => Math.max(0, c + (optimistic ? 1 : -1)));
    try {
      const r = await api.post(`/properties/${p.id}/save`);
      setSaved(!!r.data.saved);
      setSavedCount(r.data.saved_count || 0);
      toast.success(r.data.saved ? "Saved" : "Removed from saved");
      onSaveChange?.(p.id, !!r.data.saved);
    } catch (err) {
      // Roll back on failure
      setSaved(!optimistic);
      setSavedCount((c) => Math.max(0, c + (optimistic ? -1 : 1)));
      toast.error("Couldn't update saved");
    }
    setSavingFav(false);
  };

  const onCardClick = (e) => {
    // v2.71.5 — Property detail pages are publicly viewable. Just navigate —
    // no AuthGate interruption. Save / Enquiry buttons inside the detail
    // page handle their own soft-prompts when needed.
    // v2.71.78 — Save the clicked card's id keyed by the listing URL so the
    // back-nav scroll restorer can scroll it precisely into view. This is
    // more reliable than Y-based restoration because it survives lazy-image
    // layout shifts on the listing page. Read by ScrollToTop.jsx on POP.
    try {
      const key = `he-last-card:${window.location.pathname}${window.location.search}`;
      sessionStorage.setItem(key, p.id);
    } catch (_) { /* sessionStorage disabled — silent */ }
    nav(`/property/${p.id}`);
  };

  // v2.78 Phase A — Canonical display values (read-time normalisation).
  // The raw DB fields (p.bhk = "2BHK", p.property_type = "Flat") are
  // left untouched server-side; we just show the canonical spelling
  // ("2 BHK", "Apartment") to the user.
  const bhkDisplay = normalizeBhk(p.bhk);
  const propertyTypeDisplay = normalizePropertyType(p.property_type);

  // v2.78 Phase A — Tag dedup. Boolean curation flags (best_deal,
  // premium_deal, featured) are the source of truth; if `tags` array
  // ALSO contains the same label we drop the duplicate. "Verified" is
  // always force-prepended for single-brand feel.
  const computedTags = buildDisplayTags(p);
  const tags = ["Verified", ...computedTags.filter((t) => t !== "Verified")];

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={onCardClick}
        onKeyDown={(e) => { if (e.key === "Enter") onCardClick(e); }}
        className={`group prop-card block bg-white border border-slate-200 rounded-2xl overflow-hidden cursor-pointer shadow-[0_1px_3px_rgba(15,23,42,0.04)] hover:shadow-[0_8px_24px_-8px_rgba(15,23,42,0.15),0_2px_6px_-2px_rgba(15,23,42,0.08)] hover:border-slate-300 hover:-translate-y-0.5 transition-all duration-300 ease-out ${animate ? "rise" : ""} ${isSold ? "opacity-90" : ""}`}
        style={animate ? { animationDelay: `${Math.min(index * 40, 300)}ms` } : undefined}
        data-testid={`property-card-${p.id}`}
      >
        {/* v2.71.80 — Softer neutral placeholder. The old dark gradient
            (#1A1A1F → #09090B) caused a jarring "black flash" while the
            image loaded. Light neutral keeps the silhouette of the card
            without yelling at the user. */}
        <div className="relative aspect-[4/3] bg-[#F4F4F5] overflow-hidden">
          {img ? (
            <img
              loading="lazy"
              decoding="async"
              width="800"
              height="600"
              src={img}
              srcSet={imgSrcSet || undefined}
              sizes="(min-width: 1024px) 400px, (min-width: 640px) 50vw, 100vw"
              alt={p.title}
              className={`relative w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${isSold ? "grayscale" : ""}`}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#A1A1AA] bg-[#F4F4F5]"><Home size={48} strokeWidth={1} /></div>
          )}
          {isSold && (
            <div className="absolute inset-0 flex items-center justify-center" data-testid={`sold-overlay-${p.id}`}>
              <div className="bg-[#09090B] text-white px-4 py-2 rounded-sm rotate-[-8deg] border-2 border-white shadow-xl">
                <div className="text-[10px] uppercase tracking-[0.18em] opacity-70 font-semibold">Sold Out</div>
                <div className="font-heading font-bold text-xl">Closed Deal</div>
              </div>
            </div>
          )}
          <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap max-w-[80%]">
            {p.listing_intent && p.listing_intent !== "sale" && (
              <span
                className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-[#0055FF] text-white border border-[#0055FF] shadow-sm"
                data-testid={`intent-badge-${p.id}`}
              >
                For {p.listing_intent}
              </span>
            )}
            {tags.slice(0, 3).map((t) => <TagPill key={t} label={t} />)}
          </div>
          {/* ROI badge — hidden for anonymous users (preview mode) */}
          {!isSold && !isAnon && (
            <div className="absolute bottom-3 right-3 bg-[#09090B] text-white px-2.5 py-1 rounded-sm" data-testid={`roi-badge-${p.id}`}>
              <div className="text-[9px] uppercase tracking-wider text-white/60 font-semibold leading-none">ROI</div>
              <div className="text-[13px] font-bold font-mono-data leading-tight">{p.roi?.toFixed(1) ?? "—"}%</div>
            </div>
          )}
          <div className="absolute top-3 right-3 flex flex-col gap-2 sm:gap-1.5 items-end">
            <button
              type="button" onClick={onShare} aria-label="Share on WhatsApp"
              className="w-9 h-9 sm:w-8 sm:h-8 no-touch-min bg-white/95 hover:bg-[#16A34A] hover:text-white text-[#16A34A] border border-[#16A34A] rounded-full flex items-center justify-center transition-colors shadow-sm"
              data-testid={`card-wa-share-${p.id}`}
            >
              <MessageCircle size={15} />
            </button>
            <button
              type="button" onClick={onToggleSave} disabled={savingFav}
              aria-label={saved ? "Remove from saved" : "Save property"}
              aria-pressed={saved}
              className={`w-9 h-9 sm:w-8 sm:h-8 no-touch-min border rounded-full flex items-center justify-center transition-colors shadow-sm ${
                saved
                  ? "bg-[#DC2626] text-white border-[#DC2626]"
                  : "bg-white/95 text-[#DC2626] border-[#DC2626] hover:bg-[#DC2626] hover:text-white"
              }`}
              data-testid={`card-save-btn-${p.id}`}
            >
              <Heart size={15} fill={saved ? "currentColor" : "none"} />
            </button>
            {/* v2.71.13 — Add to Shortlist (admin/team only, hidden for clients) */}
            <AddToShortlistButton propertyId={p.id} propertyTitle={p.title} compact />
            {savedCount > 0 && (
              <span
                className="text-[9px] uppercase tracking-wider font-semibold text-white bg-black/65 px-1.5 py-0.5 rounded-sm leading-tight"
                data-testid={`saved-count-${p.id}`}
              >
                {savedCount} saved
              </span>
            )}
          </div>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.12em] text-[#A1A1AA] font-semibold mb-1">
              <MapPin size={11} /> {p.main_area || p.area}{p.sub_area ? ` · ${p.sub_area}` : ""}{bhkDisplay && bhkDisplay !== "Not Specified" ? ` · ${bhkDisplay}` : ""}{propertyTypeDisplay ? ` · ${propertyTypeDisplay}` : ""}
            </div>
            <h3 className="font-heading font-semibold text-[15px] tracking-tight text-[#09090B] leading-snug line-clamp-2">{p.title}</h3>
          </div>
          <div className="flex items-end justify-between pt-2 border-t border-[#F4F4F5]">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#A1A1AA] font-semibold">Price</div>
              <div className="font-heading font-bold text-xl text-[#0055FF] font-mono-data leading-none mt-0.5">{formatINR(p.price)}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-[#A1A1AA] font-semibold">Metro</div>
              <div className="text-sm font-semibold text-[#09090B] font-mono-data">{formatDistance(p.distance_metro_m)}</div>
            </div>
          </div>
          <div className="flex items-center justify-between pt-1">
            <div className="text-[11px] text-[#52525B]" data-testid={`size-display-${p.id}`}>
              <span className="font-semibold text-[#09090B] font-mono-data">{formatSize(p.size_sqft, p.size_unit)}</span>
              {p.floor ? <span className="mx-1.5 text-[#D4D4D8]">·</span> : null}
              {p.floor || ""}
            </div>
            {isSold ? (
              <span className="text-[11px] font-semibold text-[#15803D] inline-flex items-center gap-1">
                <CheckCircle size={11} /> {p.sold_in_days ? `Sold in ${p.sold_in_days}d` : "Recently sold"}
              </span>
            ) : (
              <span className="text-[12px] font-semibold text-[#0055FF] group-hover:underline underline-offset-4 inline-flex items-center" data-testid={`view-details-${p.id}`}>
                View Details →
              </span>
            )}
          </div>
          <div className="text-[10.5px] text-[#15803D] font-semibold inline-flex items-center gap-1 pt-0.5" data-testid={`verified-badge-${p.id}`}>
            <BadgeCheck size={11} /> Verified by House Entry
          </div>
        </div>
      </div>
      <AuthGate open={showGate} onClose={() => setShowGate(false)} action="details" returnTo={`/property/${p.id}`} />
    </>
  );
});
